export interface AttributionPayload {
  landingPage?: string
  currentUrl?: string
  referrer?: string
  firstSeenAt?: string
  latestSeenAt?: string
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
  utmTerm?: string
  utmContent?: string
  utmId?: string
  gclid?: string
  fbclid?: string
  msclkid?: string
  ttclid?: string
  liFatId?: string
}

const FIRST_TOUCH_KEY = 'vte_attribution_first_touch'
const LATEST_TOUCH_KEY = 'vte_attribution_latest_touch'

const QUERY_FIELD_MAP: Record<string, keyof AttributionPayload> = {
  utm_source: 'utmSource',
  utm_medium: 'utmMedium',
  utm_campaign: 'utmCampaign',
  utm_term: 'utmTerm',
  utm_content: 'utmContent',
  utm_id: 'utmId',
  gclid: 'gclid',
  fbclid: 'fbclid',
  msclkid: 'msclkid',
  ttclid: 'ttclid',
  li_fat_id: 'liFatId',
}

function readStoredAttribution(key: string): AttributionPayload | null {
  try {
    const raw = localStorage.getItem(key)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

function hasCampaignSignal(payload: AttributionPayload): boolean {
  return Object.keys(QUERY_FIELD_MAP).some((queryKey) => {
    const field = QUERY_FIELD_MAP[queryKey]
    return Boolean(payload[field])
  })
}

function collectCurrentAttribution(): AttributionPayload {
  const url = new URL(window.location.href)

  // The marketing landing (virtualtoureasy.com) forwards the visitor's original
  // pre-landing referrer + entry page as query params, because they're on a
  // different origin and would otherwise be lost — document.referrer here is just
  // the landing page. Prefer the forwarded values when present.
  const forwardedReferrer = url.searchParams.get('ref') || undefined
  const forwardedLanding = url.searchParams.get('landing_page') || undefined

  const payload: AttributionPayload = {
    landingPage: forwardedLanding || window.location.href,
    currentUrl: window.location.href,
    referrer: forwardedReferrer || document.referrer || undefined,
    firstSeenAt: new Date().toISOString(),
    latestSeenAt: new Date().toISOString(),
  }

  for (const [queryKey, payloadKey] of Object.entries(QUERY_FIELD_MAP)) {
    const value = url.searchParams.get(queryKey)
    if (value) payload[payloadKey] = value
  }

  return payload
}

export function captureAttribution() {
  if (typeof window === 'undefined') return

  const current = collectCurrentAttribution()
  const firstTouch = readStoredAttribution(FIRST_TOUCH_KEY)

  if (!firstTouch) {
    localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(current))
  }

  if (hasCampaignSignal(current) || current.referrer) {
    localStorage.setItem(LATEST_TOUCH_KEY, JSON.stringify(current))
  }
}

export function getRegistrationAttribution(): AttributionPayload | undefined {
  if (typeof window === 'undefined') return undefined

  captureAttribution()

  const firstTouch = readStoredAttribution(FIRST_TOUCH_KEY)
  const latestTouch = readStoredAttribution(LATEST_TOUCH_KEY)
  if (!firstTouch && !latestTouch) return undefined

  return {
    ...firstTouch,
    ...latestTouch,
    landingPage: firstTouch?.landingPage || latestTouch?.landingPage,
    referrer: firstTouch?.referrer || latestTouch?.referrer,
    firstSeenAt: firstTouch?.firstSeenAt || latestTouch?.firstSeenAt,
    currentUrl: window.location.href,
    latestSeenAt: new Date().toISOString(),
  }
}
